import { useUser } from "@modules/auth/hooks/useUser"
import { useSelectedBoxId } from "@modules/box/useBox"
import { Path } from "@modules/navigation/routes"
import { useBoxNavigate } from "@modules/navigation/useBoxNavigate"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUImage } from "@modules/types/imageTypes"
import { HuFlag } from "@modules/ui/components/huFlag"
import { HUText } from "@modules/ui/components/huText"
import { Mail, MessageCircleMore } from "lucide-react"
import { Skeleton } from "primereact/skeleton"
import { Link, useNavigate } from "react-router-dom"
import { useTheme } from "styled-components"

export type HuAvatarCardSidebarProps = {
  id?: string
  avatar?: HUImage
  firstname?: string
  lastname?: string
  username?: string
  nationality?: string
  email?: string
  logo?: React.JSX.Element
  className?: string
  style?: React.CSSProperties
  prefixUsername?: string
  onAvatarClick?: () => void
  canUseContact?: boolean
  isLoading?: boolean
  topContent?: React.JSX.Element
  bottomButton?: React.JSX.Element
} & React.HTMLAttributes<HTMLDivElement>

/**
 * HuAvatarCardSidebar component renders a sidebar card with user avatar and details.
 * It displays a skeleton loader when data is being fetched.
 *
 * @component
 * @param {HuAvatarCardSidebarProps} props - The properties for the component.
 * @param {string} props.id - The unique identifier for the user.
 * @param {string} props.avatar - The URL of the user's avatar image.
 * @param {string} props.firstname - The first name of the user.
 * @param {string} props.lastname - The last name of the user.
 * @param {string} props.username - The username of the user.
 * @param {string} props.nationality - The nationality of the user.
 * @param {string} props.email - The email address of the user.
 * @param {string} props.className - Additional CSS class for the component.
 * @param {React.CSSProperties} props.style - Additional styles for the component.
 * @param {string} props.prefixUsername - The prefix for the username. Usually we use @
 * @param {React.JSX.Element} props.logo - The logo on top of the user name
 * @param {() => void} props.onAvatarClick - The function to call when the avatar is clicked.
 * @param {boolean} props.isLoading - Flag indicating if the data is being loaded.
 * @param {boolean} props.canUseContact - Flag indicating if the user can be contacted.
 * @param {React.JSX.Element} props.topContent - The content at the top of the card.
 * @param {React.JSX.Element} props.bottomButton - The button at the bottom of the card.
 * @returns {React.JSX.Element} The rendered component.
 */
export const HuAvatarCardSidebar: React.FC<HuAvatarCardSidebarProps> = ({
  id,
  avatar,
  firstname,
  lastname,
  username,
  nationality,
  email,
  className,
  style,
  prefixUsername,
  isLoading,
  logo,
  onAvatarClick,
  canUseContact = true,
  bottomButton,
  topContent,
  ...props
}) => {
  const defaultClassName = "relative"
  const defaultStyle = { marginTop: "-85px", marginLeft: "-20px", marginRight: "-20px" }
  const theme = useTheme()
  const { hideSidebar } = useSidebar()
  const boxId = useSelectedBoxId()
  const boxNavigate = useBoxNavigate()
  const navigate = useNavigate()
  const user = useUser()

  const openChatWithUser = async () => {
    if (id) {
      if (boxId) {
        hideSidebar()
        boxNavigate(Path.Chat.Root, { state: { userToChatTo: id } })
      } else {
        hideSidebar()
        navigate(Path.Chat.Root, { state: { userToChatTo: id } })
      }
    }
  }

  // SKELETON
  if (isLoading) {
    return (
      <div className={className ?? defaultClassName} style={style ?? defaultStyle}>
        <div className="relative flex w-full h-16rem bg-neutralColor-50 border-round-bottom-3xl" {...props}>
          <div className="flex gap-3 justify-content-center align-items-center pt-6 w-full">
            <div className="relative">
              <Skeleton shape="circle" size="6rem" className="mb-1" />
            </div>
            <div className="flex  flex-column justify-content-center align-items-center gap-2 ">
              <Skeleton width="10rem" height="2rem" borderRadius="8px" />
              <Skeleton width="5rem" height="1rem" borderRadius="4px" />
            </div>
          </div>

          <div className="absolute flex flex-column gap-1 right-0 mr-3" style={{ marginTop: "7rem" }}>
            <Skeleton shape="circle" size="2rem" className="p-2" />
            <Skeleton shape="circle" size="2rem" className="p-2" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative" style={{ marginTop: "-85px", marginLeft: "-20px", marginRight: "-20px" }}>
      <div
        className="flex flex-column w-full h-16rem align-items-center justify-content-center bg-neutralColor-50 border-round-bottom-3xl"
        {...props}
      >
        {/* TOP CONTENT */}
        {topContent}

        <div className="flex gap-3 justify-content-center align-items-center pt-6 w-full">
          {/* PICTURE PROFIL */}
          <div className={"relative" + (!!onAvatarClick ? " cursor-pointer" : "")} onClick={onAvatarClick}>
            <ProfileAvatar
              className="mb-1"
              profile={{
                avatar,
                username: username || firstname || "",
              }}
              size={90}
            />
            {nationality && (
              <div className="absolute bottom-0 " style={{ left: "40%" }}>
                <HuFlag
                  className="border-neutral-700 border-1 border-round-lg"
                  countryCode={nationality || ""}
                  svg={true}
                  style={{ width: "1.4rem", height: "1.2rem", objectFit: "cover" }}
                />
              </div>
            )}
          </div>

          {/* LOGO NAME PSEUDO */}
          <div
            className={
              "flex flex-column align-items-center mr-4 max-w-20rem" + (!!onAvatarClick ? " cursor-pointer" : "")
            }
            onClick={onAvatarClick}
          >
            {logo}

            <HUText
              fontStyle="HS"
              color={theme.blackAtheleWhiteOwner}
              className="capitalize"
              style={{ whiteSpace: "break-spaces", overflowWrap: "anywhere" }}
              numberOfLine={2}
              title={firstname + " " + lastname}
            >
              {firstname + " " + lastname}
            </HUText>
            <HUText
              fontStyle="BM"
              color={theme.neutralColor700}
              className="capitalize"
              style={{ whiteSpace: "break-spaces", overflowWrap: "anywhere" }}
              numberOfLine={2}
            >
              {prefixUsername ?? ""}
              {username}
            </HUText>
          </div>
        </div>

        {/* BUTTOM BUTTON */}
        {bottomButton}

        {/* CONTACT ICONS */}
        {user && (user.id === id || !canUseContact) ? null : (
          <div className="absolute flex flex-column gap-1 right-0 mr-3" style={{ marginTop: "3rem" }}>
            <MessageCircleMore
              cursor="pointer"
              className="bg-neutralColor-0 rounded-full p-2"
              strokeWidth={2}
              size={35}
              color={theme.blackAtheleWhiteOwner}
              onClick={openChatWithUser}
            />

            {email && (
              <Link
                to="#"
                onClick={(e) => {
                  window.location.href = `mailto:${email}`
                  e.preventDefault()
                }}
              >
                <Mail
                  className="bg-neutralColor-0 rounded-full p-2"
                  strokeWidth={2}
                  size={35}
                  color={theme.blackAtheleWhiteOwner}
                />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
